import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {IReactionDisposer, makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPromotionStore} from "data/stores/promotion/promotion.store";
import type {IUserStore} from "data/stores/user/user.store";
import {GameLevelStatus, ModalType, GameLevel} from "data/enums";
import {noop, pick} from "lodash";
import {Empty} from "data/types/global";
import {useLocation} from "react-router";
import {wait, trackSentryErrors} from "data/utils";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

interface IParams {
	location: ReturnType<typeof useLocation>;
}

export interface IModalPromotedController extends ViewController<IParams> {
	close: () => Promise<void>;

	get i18n(): ILocalizationStore;

	get isOpen(): boolean;

	get confettiState(): boolean;

	get isPromoted(): boolean;

	get isDemoted(): boolean;

	get points(): number;

	get level(): GameLevel;

	get levelStatus(): Empty<GameLevelStatus>;
}

@injectable()
export class ModalPromotedController implements IModalPromotedController {
	@observable private _subscriptions$: IReactionDisposer[] = [];
	@observable private _pathName: IParams["location"]["pathname"] = "";
	@observable private _confettiState: boolean = false;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.PromotionStore) private readonly _promotionStore: IPromotionStore,
		@inject(Bindings.RoundsStore) private readonly _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get confettiState() {
		return this._confettiState;
	}

	get isPromoted(): boolean {
		return this.roundInfo?.levelStatus === GameLevelStatus.Promoted;
	}

	get isDemoted(): boolean {
		return this.roundInfo?.levelStatus === GameLevelStatus.Demoted;
	}

	get points(): number {
		return this.roundInfo?.points || 0;
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.PROMOTED;
	}

	get level(): GameLevel {
		return this.roundInfo?.level || GameLevel.SINGLE_A;
	}

	get levelStatus(): Empty<GameLevelStatus> {
		return this.roundInfo?.levelStatus;
	}

	private get roundInfo() {
		return this._promotionStore.roundInfo;
	}

	private get user() {
		return this._userStore.user;
	}

	private get isLevelChanged(): boolean {
		return this.isPromoted || this.isDemoted;
	}

	dispose(): void {
		this._subscriptions$.forEach((dispose) => dispose());
	}

	init(param: IParams): void {
		this._pathName = param.location.pathname;
		const roundSubscription = reaction(
			() => this._userStore.user?.isPromotionDemotionModalViewed,
			() => this.checkFetchRound(),
			{fireImmediately: true}
		);
		const roundInfoSubscription = reaction(
			() => this.roundInfo,
			() => this.checkModalState(),
			{fireImmediately: true}
		);
		this._subscriptions$.push(roundSubscription);
		this._subscriptions$.push(roundInfoSubscription);
	}

	onChange(param: IParams): void {
		this._pathName = param.location.pathname;
		this.checkModalState();
	}

	public close = async () => {
		await this._promotionStore
			.promotionViewed()
			.then(() => {
				this._userStore.setPromotionAsViewed();
			})
			.catch(noop);
		this._modalsStore.hideModal();
	};

	private checkFetchRound() {
		const rounds = this._roundsStore.pastRounds;
		if (!this.user || !rounds.length) {
			return;
		}
		if (!this.user.isPromotionDemotionModalViewed) {
			this._promotionStore.fetchLastCompleteRoundInfo().catch((e) => {
				trackSentryErrors(e, pick(this.user, "id"), "fetchLastCompleteRoundInfo");
			});
		}
	}

	private get isLandingPage() {
		return this._pathName === "/";
	}

	private checkModalState() {
		const isViewed = this.user?.isPromotionDemotionModalViewed;
		if (!isViewed && this.isLevelChanged && !this.isLandingPage) {
			this._modalsStore.showModal(ModalType.PROMOTED);
			if (this.isPromoted) {
				this._confettiState = true;
				void wait(20000).then(() => {
					runInAction(() => {
						this._confettiState = false;
					});
				});
			}
		}
	}
}
